/**
 * Shared utilities for data-capture configuration in the registration wizard.
 *
 * Deduplicates visibility / collapsed / label resolution logic that was
 * previously duplicated across StepCustomer and StepPurchaseCustomer.
 */

import { useState, useCallback } from 'react'
import type { DataCaptureConfig, DataCaptureMode } from '@/admin/hooks/use-registration-settings'

export function isVisible(mode: DataCaptureMode | undefined): boolean {
  return mode !== 'EXCLUDED'
}

export function isCollapsed(mode: DataCaptureMode | undefined): boolean {
  return mode === 'COLLAPSED'
}

export function isRequired(mode: DataCaptureMode | undefined): boolean {
  return mode === 'REQUIRED'
}

export function useDataCaptureConfig(dataCaptureConfig?: DataCaptureConfig) {
  const dcc = dataCaptureConfig

  const [expandedFields, setExpandedFields] = useState<Set<string>>(() => new Set())

  const toggleField = useCallback((field: string) => {
    setExpandedFields((prev) => {
      const next = new Set(prev)
      if (next.has(field)) next.delete(field)
      else next.add(field)
      return next
    })
  }, [])

  const isFieldShown = useCallback(
    (field: string, mode: DataCaptureMode | undefined) => {
      if (!isVisible(mode)) return false
      if (isCollapsed(mode)) return expandedFields.has(field)
      return true
    },
    [expandedFields],
  )

  /** Resolve custom label from field_overrides, falling back to the default. */
  const fieldLabel = useCallback(
    (key: string, fallback: string) =>
      dcc?.field_overrides?.[key]?.label || fallback,
    [dcc?.field_overrides],
  )

  return { dcc, expandedFields, toggleField, isFieldShown, fieldLabel }
}
